import { useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    setWaypoints,
    setIsRerouting,
    selectRouteCoordinates,
    selectIsRerouting
} from '../mapSlice';
import { addToast } from '../../ui/toastSlice';

/**
 * Custom hook to handle route rerouting logic
 * Used in both debug simulation and production GPS tracking
 */
export const useRerouting = () => {
    const dispatch = useDispatch();
    const routeCoordinates = useSelector(selectRouteCoordinates);
    const isRerouting = useSelector(selectIsRerouting);
    const { t } = useTranslation();

    const destinationRef = useRef(null);

    // Update destination when route changes
    const updateDestination = useCallback(() => {
        if (routeCoordinates && routeCoordinates.length > 0) {
            destinationRef.current = routeCoordinates[routeCoordinates.length - 1];
        }
    }, [routeCoordinates]);

    const triggerReroute = useCallback((currentLocation, onPauseCallback) => {
        if (isRerouting || !destinationRef.current) return;

        // Pause any ongoing activity (simulation, GPS tracking, etc.)
        if (onPauseCallback) {
            onPauseCallback();
        }

        // Set rerouting flag
        dispatch(setIsRerouting(true));

        // Show toast notification
        dispatch(addToast({ message: t('hud.rerouting'), type: 'info', duration: 3000 }));

        // Update waypoints to trigger new route calculation
        dispatch(setWaypoints([currentLocation, destinationRef.current]));
    }, [isRerouting, dispatch, t]);

    return {
        updateDestination,
        triggerReroute,
        destinationRef
    };
};
